import { useState } from "react";
import { toast } from "@telia/teddy";
import {
    API_URL_REACTIVATE_SALES_AGENT,
    API_URL_DEACTIVATE_SALES_AGENT,
    API_URL_DELETE_SALES_AGENT_EMPLOYMENT,
} from "../api";
import { retailerFetch } from "../retailerFetch";
import { SalesAgent } from "../types/models";

interface UseSalesAgentActionsResult {
    activating: number | null;
    deactivating: number | null;
    deletingEmployment: number | null;
    activateError: string | null;
    handleActivate: (agent: SalesAgent) => void;
    handleDeactivate: (agent: SalesAgent) => void;
    handleDeleteEmployment: (agent: SalesAgent) => void;
}

export const useSalesAgentActions = (
    setAgents: React.Dispatch<React.SetStateAction<SalesAgent[]>>,
): UseSalesAgentActionsResult => {
    const [activating, setActivating] = useState<number | null>(null);
    const [deactivating, setDeactivating] = useState<number | null>(null);
    const [deletingEmployment, setDeletingEmployment] = useState<number | null>(null);
    const [activateError, setActivateError] = useState<string | null>(null);

    const handleActivate = async (agent: SalesAgent) => {
        setActivating(agent.id);
        setActivateError(null);
        try {
            const res = await retailerFetch(API_URL_REACTIVATE_SALES_AGENT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ salesAgentId: agent.id }),
            });

            if (!res.ok) throw new Error(`Feil ved aktivering (${res.status})`);

            setAgents((prev) => prev.map((a) => (a.id === agent.id ? { ...a, active: true } : a)));
            toast.success("Selger aktivert", { position: "top-center" });
        } catch (err: unknown) {
            setActivateError(err instanceof Error ? err.message : "Ukjent feil ved aktivering");
        } finally {
            setActivating(null);
        }
    };

    const handleDeactivate = async (agent: SalesAgent) => {
        setDeactivating(agent.id);
        setActivateError(null);
        try {
            const res = await retailerFetch(API_URL_DEACTIVATE_SALES_AGENT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ salesAgentId: agent.id }),
            });

            if (!res.ok) throw new Error(`Feil ved deaktivering (${res.status})`);

            setAgents((prev) => prev.map((a) => (a.id === agent.id ? { ...a, active: false } : a)));
            toast.success("Selger deaktivert", { position: "top-center" });
        } catch (err: unknown) {
            setActivateError(err instanceof Error ? err.message : "Ukjent feil ved deaktivering");
        } finally {
            setDeactivating(null);
        }
    };

    const handleDeleteEmployment = async (agent: SalesAgent) => {
        setDeletingEmployment(agent.id);
        setActivateError(null);
        try {
            const res = await retailerFetch(API_URL_DELETE_SALES_AGENT_EMPLOYMENT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ salesAgentId: agent.id }),
            });

            if (!res.ok) throw new Error(`Feil ved fjerning fra butikk (${res.status})`);

            setAgents((prev) => prev.filter((a) => a.id !== agent.id));
            toast.success("Selger fjernet fra butikk", { position: "top-center" });
        } catch (err: unknown) {
            setActivateError(err instanceof Error ? err.message : "Ukjent feil ved fjerning fra butikk");
        } finally {
            setDeletingEmployment(null);
        }
    };

    return {
        activating,
        deactivating,
        deletingEmployment,
        activateError,
        handleActivate,
        handleDeactivate,
        handleDeleteEmployment,
    };
};
